import { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    AutoAwesomeOutlined as PlanIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { dialogOpen, dialogClose } from '@/modules/MuiDialog';
import { formatLocaleDate } from '@/utils/formatter';
import makeErrorString from '@/utils/makeErrorString';

import { useMySubscription } from '@/hooks/queries/subscription/useMySubscription';
import { useUsage } from '@/hooks/queries/subscription/useUsage';
import { useCancelSubscription } from '@/hooks/queries/subscription/useCancelSubscription';
import { useResumeSubscription } from '@/hooks/queries/subscription/useResumeSubscription';
import { useCancelScheduledPlanChange } from '@/hooks/queries/subscription/useCancelScheduledPlanChange';
import { useSubscriptionPlans } from '@/hooks/queries/subscription/useSubscriptionPlans';
import type { SubscriptionDto } from '@/types/subscription';

import { ACCENT, ACCENT_HOVER, ACCENT_TINT } from '../_shared';
import { parseUsage, STATUS_LABEL } from './helpers';

interface CancelDialogBodyProps {
    onConfirm: (reason: string) => void | Promise<void>;
    onCancel: () => void;
}

const CancelDialogBody = ({ onConfirm, onCancel }: CancelDialogBodyProps) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm(reason.trim());
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <DialogTitle sx={{ fontWeight: 800 }}>구독을 해지할까요?</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5} sx={{ pt: 0.5, minWidth: { sm: 360 } }}>
                    <Typography variant="body2" color="text.secondary">
                        다음 결제일에 자동 결제가 멈추고 이용 기간 종료까지 그대로 사용할 수 있어요.
                    </Typography>
                    <TextField
                        label="해지 사유 (선택)"
                        placeholder="더 나은 서비스를 위해 알려주세요"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onCancel}
                    disabled={submitting}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: 2, textTransform: 'none', cursor: 'pointer' }}
                >
                    취소
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={submitting}
                    variant="contained"
                    color="error"
                    disableElevation
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={{ borderRadius: 2, textTransform: 'none', cursor: 'pointer' }}
                >
                    {submitting ? '처리 중…' : '해지 예약'}
                </Button>
            </DialogActions>
        </>
    );
};

const CurrentSubscriptionSection = () => {
    const { data: subscription, isLoading } = useMySubscription();
    const { data: usage } = useUsage();
    const { data: plans } = useSubscriptionPlans();
    const cancel = useCancelSubscription();
    const resume = useResumeSubscription();
    const cancelScheduled = useCancelScheduledPlanChange();

    if (isLoading) {
        return (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                    <Skeleton variant="text" width="40%" height={28} />
                    <Skeleton variant="text" width="30%" height={48} sx={{ my: 1 }} />
                    <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                </CardContent>
            </Card>
        );
    }

    // 구독 없음 — 안내 카드
    if (!subscription) {
        return (
            <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: ACCENT_TINT }}>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <InfoIcon sx={{ color: ACCENT }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            아직 구독 중이 아니에요
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        아래 플랜을 선택해 시작해보세요.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const isActive = subscription.status === 'active';
    const isFreePlan = subscription.plan?.code === 'free';
    const isCancelScheduled = isActive && !!subscription.cancelAt;
    const isPlanChangeScheduled = isActive && !!subscription.scheduledPlanId;
    const scheduledPlan = isPlanChangeScheduled
        ? plans?.find((p) => p.id === subscription.scheduledPlanId)
        : undefined;
    const isEnded = subscription.status === 'cancelled' || subscription.status === 'expired';

    const handleCancel = () => {
        dialogOpen({
            maxWidth: 'xs' as any,
            fullWidth: true,
            innerElement: (
                <CancelDialogBody
                    onCancel={() => dialogClose()}
                    onConfirm={async (reason) => {
                        try {
                            await cancel.mutateAsync({ reason: reason || undefined });
                            toast.success('해지가 예약되었어요');
                            dialogClose();
                        } catch (e) {
                            toast.error(makeErrorString(e, '해지를 처리하지 못했어요'));
                        }
                    }}
                />
            ),
        });
    };

    const handleResume = async () => {
        try {
            await resume.mutateAsync();
            toast.success('해지를 철회했어요');
        } catch (e) {
            toast.error(makeErrorString(e, '해지 철회에 실패했어요'));
        }
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                background: isActive ? 'linear-gradient(135deg,#fff8f2 0%,#ffe9d9 100%)' : '#fff',
                borderColor: isActive ? '#ffd2b8' : 'divider',
            }}
        >
            <CardContent sx={{ py: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <PlanIcon sx={{ color: ACCENT, fontSize: 22 }} />
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {subscription.plan?.name ?? '구독'}
                            </Typography>
                            <Chip
                                size="small"
                                label={STATUS_LABEL[subscription.status] ?? subscription.status}
                                sx={{
                                    bgcolor: isActive ? ACCENT : 'grey.300',
                                    color: isActive ? '#fff' : 'text.secondary',
                                    fontWeight: 700,
                                }}
                            />
                            {isCancelScheduled && (
                                <Chip
                                    size="small"
                                    label="해지 예약"
                                    sx={{ bgcolor: 'error.main', color: '#fff', fontWeight: 700 }}
                                />
                            )}
                        </Stack>

                        <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: ACCENT }}>
                                {subscription.amount === 0
                                    ? '무료'
                                    : `₩${subscription.amount.toLocaleString('ko-KR')} / ${
                                          subscription.billingCycle === 'yearly' ? '년' : '월'
                                      }`}
                            </Typography>
                            <Chip
                                size="small"
                                label={subscription.billingCycle === 'yearly' ? '연 결제' : '월 결제'}
                                sx={{
                                    ml: 0.5,
                                    height: 20,
                                    fontWeight: 600,
                                    bgcolor: 'rgba(0,0,0,0.06)',
                                    color: 'text.secondary',
                                }}
                            />
                        </Stack>

                        {!isFreePlan && (
                            <Typography variant="body2" color="text.secondary">
                                이용 기간: {formatLocaleDate(subscription.currentPeriodStart)} ~{' '}
                                {formatLocaleDate(subscription.currentPeriodEnd)}
                            </Typography>
                        )}
                        {!isFreePlan && isActive && !isCancelScheduled && subscription.nextBillingDate && (
                            <Typography variant="body2" color="text.secondary">
                                다음 결제: {formatLocaleDate(subscription.nextBillingDate)}
                            </Typography>
                        )}
                        {isCancelScheduled && subscription.cancelAt && (
                            <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
                                {formatLocaleDate(subscription.cancelAt)}에 자동 해지될 예정이에요
                            </Typography>
                        )}
                        {isPlanChangeScheduled && !isCancelScheduled && subscription.scheduledAt && (
                            <Typography variant="body2" sx={{ color: ACCENT, mt: 0.5 }}>
                                {formatLocaleDate(subscription.scheduledAt)}에{' '}
                                {scheduledPlan?.name ?? '다른 플랜'}으로 변경 예약돼 있어요
                            </Typography>
                        )}
                    </Box>

                    {isActive && !isFreePlan && !isCancelScheduled && !isPlanChangeScheduled && (
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            color="inherit"
                            disabled={cancel.isPending}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                px: 2.5,
                                cursor: 'pointer',
                                color: 'text.secondary',
                                borderColor: 'divider',
                                transition: 'transform 120ms ease',
                                '&:hover': { transform: 'translateY(-1px)' },
                            }}
                        >
                            구독 해지
                        </Button>
                    )}
                    {isCancelScheduled && (
                        <Button
                            onClick={handleResume}
                            variant="contained"
                            disableElevation
                            disabled={resume.isPending}
                            sx={{
                                bgcolor: ACCENT,
                                color: '#fff',
                                borderRadius: 2,
                                textTransform: 'none',
                                px: 2.5,
                                cursor: 'pointer',
                                transition: 'transform 120ms ease, background-color 120ms ease',
                                '&:hover': { bgcolor: ACCENT_HOVER, transform: 'translateY(-1px)' },
                            }}
                        >
                            해지 철회
                        </Button>
                    )}
                    {isPlanChangeScheduled && !isCancelScheduled && (
                        <Button
                            onClick={async () => {
                                try {
                                    await cancelScheduled.mutateAsync();
                                    toast.success('변경 예약을 취소했어요');
                                } catch (e) {
                                    toast.error(makeErrorString(e, '예약 취소에 실패했어요'));
                                }
                            }}
                            variant="outlined"
                            color="inherit"
                            disabled={cancelScheduled.isPending}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                px: 2.5,
                                cursor: 'pointer',
                                color: 'text.secondary',
                                borderColor: 'divider',
                                transition: 'transform 120ms ease',
                                '&:hover': { transform: 'translateY(-1px)' },
                            }}
                        >
                            변경 예약 취소
                        </Button>
                    )}
                </Stack>

                {/* 사용량 */}
                {isActive && usage && usage.length > 0 && (
                    <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                        {usage.map((u) => {
                            const { used, limit, ratio } = parseUsage(u.used_amount, u.limit_amount);
                            const unlimited = limit === null;
                            return (
                                <Box key={u.id}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="baseline"
                                        sx={{ mb: 0.5 }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {u.feature_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {used.toLocaleString('ko-KR')}{' '}
                                            {unlimited ? '/ 무제한' : `/ ${(limit as number).toLocaleString('ko-KR')}`}
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={unlimited ? 0 : ratio}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            bgcolor: 'rgba(0,0,0,0.06)',
                                            '& .MuiLinearProgress-bar': {
                                                bgcolor: ratio >= 90 ? 'error.main' : ACCENT,
                                                borderRadius: 4,
                                            },
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                )}

                {isEnded && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        구독이 종료되었어요. 아래 플랜을 선택해 다시 시작할 수 있어요.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default CurrentSubscriptionSection;
